import React, { useEffect, useState } from "react";
import { useStudentActions } from "../../../../hooks/useStudentActions";
import { Assessment, Item } from "../../../../models/assessments"
import { Classroom } from "../../../../models/classroom";
import { newStudent } from "../../../../models/student";
import { Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from "@mui/material";
import { InfoOutlineRounded } from "@mui/icons-material";
import { isRichTextContentEmpty } from "../../../../utils/rich-text-empty";
import { RenderRichText } from "../../../../components/render-rich-text";
import axios from "axios";
import { useSnackbar } from "../../../../components/snackbar";
import { shuffleArray } from "../../../../utils/shuffle-array";

interface PreStartAssessmentProps {
    reg_number: string;
    assessment: Assessment;
    setStarted: React.Dispatch<React.SetStateAction<boolean>>;
    setQuestions: React.Dispatch<React.SetStateAction<Item[][] | null>>;
}

export function PreStartAssessment({ assessment, reg_number, setStarted, setQuestions }: PreStartAssessmentProps) {
    const {
        student,
        getStudent,
        startAssessment,
    } = useStudentActions();

    const [locStudent, setLocStudent] = useState(newStudent());

    const [loading, setLoading] = useState(false);

    const { showMessage } = useSnackbar();
    
    const getLocalAnswers = (questions: Item[][]) => {
        const saved = sessionStorage.getItem(`assessment-${assessment.id}-responses`);
    
        let restored = questions;

        if (saved) {
            const parsed = JSON.parse(saved);   
        
            restored = questions.map((section, sectionIndex) =>
                section.map((question) => {
                    const savedAnswer = parsed[sectionIndex]?.find((x:Item) => x.id === question.id);
                    if (savedAnswer) {
                        return { ...question, answer: savedAnswer.answer };
                    }
                    return question;
                })
            );
        }
        
        const shuffledQuestions = restored.map((section) => {
            if (!assessment.shuffle_questions) {
                return section.map((question) => {
                    if (question.shuffle_options && question.options?.length) {
                        return {
                            ...question,
                            options: shuffleArray([...question.options]),
                        };
                    }
                    return question;
                });
            }

            const staticItems: { index: number; item: Item }[] = [];
            const shuffleItems: Item[] = [];

            section.forEach((item, index) => {
                if (item.type === 0) {
                    staticItems.push({ index, item });
                } else {
                    shuffleItems.push(item);
                }
            });

            const shuffled = shuffleArray(shuffleItems);

            const result: Item[] = [];
            let shuffleIndex = 0;

            for (let i = 0; i < section.length; i++) {
                const staticItem = staticItems.find((s) => s.index === i);
                if (staticItem) {
                    result.push(staticItem.item);
                } else {
                    result.push(shuffled[shuffleIndex++]);
                }
            }

            return result.map((q) => {
                if (q.shuffle_options && q.options?.length) {
                    return {
                        ...q,
                        options: shuffleArray([...q.options]),
                    };
                }
                return q;
            });
        });

        return shuffledQuestions;
    
    }
    
    const startAss = async () => {
        setLoading(true);

        const questions = await startAssessment(assessment.classroom_id, assessment.id);

        if (questions) {
            const restored = getLocalAnswers(questions);
            
            
            if (restored) {
                setQuestions(restored);
                setStarted(true);
            };
        }
        setLoading(false);
    };

    useEffect(() => {
        const stored = localStorage.getItem("student");

        if (stored) {
            const parsed = JSON.parse(stored);
            setLocStudent(parsed);
        } else {
            const loadStudent = async () => {
            await getStudent();
            };
            loadStudent();
        }
    }, [student]);

    return (
        <Box
            sx={{
                backgroundColor: "#c6e1f5",
                minHeight: "100vh",
                display: "flex",
                padding: 1,
            }}
        >
            <Box
                sx={{
                    margin: 'auto',
                    width: '100%',
                    maxWidth: "700px",
                    backgroundColor: "#fff",
                    padding: 2,
                    borderRadius: 1,
                }}
            >

                 <Typography variant="h6" fontWeight={700} color="text.secondary">
                    {assessment.title}
                </Typography>

                <Box sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 2 }} />


                {!isRichTextContentEmpty(assessment.instruction) && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Instruction
                        </Typography>

                        <Box sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 2 }} />
                        <RenderRichText jsonContent={assessment.instruction} />
                         <Box sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 2 }} />
                    </Box>
                )}

            
                {/* Student Info & Start */}
                <Stack spacing={2}>
                    <TextField
                        value={locStudent.name}
                        fullWidth
                        disabled
                        variant="outlined"
                        sx={{ backgroundColor: "white" }}
                    />
                    <TextField
                        value={reg_number}
                        fullWidth
                        disabled
                        variant="outlined"
                        sx={{ backgroundColor: "white" }}
                    />
                    <Button
                        variant="contained"
                        size="large"
                        onClick={startAss}
                        sx={{ 
                            textTransform: "none", 
                            fontWeight: 600,
                            backgroundColor: "#059669",
                            '&:hover': {
                                backgroundColor: "#047857",
                            },
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={20} />
                            ) : (
                               "Proceed"
                        )}

                    </Button>
                </Stack>
            </Box>
        </Box>
    )
}